import {
  Component,
  OnInit,
  OnDestroy,
  HostListener,
  ElementRef,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Subject, takeUntil, combineLatest, firstValueFrom } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { FormsModule } from '@angular/forms';
import { StateService } from '@/services/state.service';
import { NotificationsService, NOTIFICATION_TYPES, NotificationType, SubscriptionState } from '@/services/notifications.service';
import { QrCodeComponent } from 'ng-qrcode';
import { environment } from 'src/environments/environment';
import { PublicNotificationChannelConfig } from 'src/environments/env.interface';
import { TippyDirective } from '@/directives/tippy.directive';
import { BidMinAmountLimitModalComponent } from '@/components/bid-min-amount-limit-modal/bid-min-amount-limit-modal.component';
import { nativeBidNotificationsLabel } from '@/utils/native-market-bid-labels';

/** Maps environment.collection to X admin API collectionId */
const ENV_COLLECTION_TO_X_COLLECTION_ID: Record<string, string> = {
  cryptopunks: 'original',
  'cryptopunks-v1': 'cryptopunks-v1',
  cryptophunks: 'crypto-phunks',
};

/** Display label per X admin collectionId */
const X_COLLECTION_LABELS: Record<string, string> = {
  original: 'Punk V2',
  'cryptopunks-v1': 'Punk V1',
  'crypto-phunks': 'Phunks',
};

interface XAdminRow {
  collectionId: string;
  label: string;
  connected: boolean;
  username?: string;
  connecting: boolean;
  disconnecting: boolean;
  testing: boolean;
}

/** Display labels for notification types */
const NOTIFICATION_TYPE_LABELS: Record<NotificationType, string> = {
  SALES: 'Sales (including 3rd party marketplaces)',
  LISTINGS: 'Listings (including 3rd party marketplaces)',
  LEGACY_BIDS: nativeBidNotificationsLabel,
  PUBLIC_STASH_BIDS: 'Public stash bids',
  CONFIDENTIAL_STASH_BIDS: 'Confidential stash bids',
  TRANSFERS: 'Transfers',
  WRAP_UNWRAP_EVENTS: 'Wrap/unwrap events',
};

const FALLBACK_PUBLIC_CHANNEL_DESCRIPTIONS: Record<PublicNotificationChannelConfig['type'], string> = {
  telegram: 'Real-time sales, bids, and listings broadcast.',
  discord: 'Marketplace announcements and community updates.',
  x: 'Live social updates from the marketplace bot.',
};

@Component({
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    QrCodeComponent,
    TippyDirective,
    BidMinAmountLimitModalComponent,
  ],
  selector: 'app-notifications',
  templateUrl: './notifications.component.html',
  styleUrls: ['./notifications.component.scss'],
})
export class NotificationsComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  isConnected = false;
  walletAddress: string | null = null;
  effectiveAddress: string | null = null;

  // Bid min amount limit modal
  minAmountLimitModalOpen = false;

  // Email
  userEmail = '';
  loadingEmail = false;
  canSaveEmail = false;

  // Telegram personal (bids on owned punks)
  telegramLinkCode = '';
  loadingTelegramLink = false;
  loadingTelegramUnlink = false;
  telegramLinked = false;
  telegramLinkIdentifier: string | null = null;
  telegramLinkError: string | null = null;

  // Subscriptions matrix
  subscriptions: Record<NotificationType, SubscriptionState> = {} as Record<
    NotificationType,
    SubscriptionState
  >;
  loadingSubscriptions = false;
  savingSubscription: { type: NotificationType; channel: 'email' | 'telegram' } | null = null;

  // X admin (single row for current marketplace)
  xAdminVisible = false;
  xAdminLoading = false;
  xAdminError: string | null = null;
  xAdminMessage: string | null = null;
  currentXAdminRow: XAdminRow | null = null;

  /** Precomputed matrix rows for template (avoids method calls in change detection) */
  matrixRows: Array<{
    type: NotificationType;
    label: string;
    emailEnabled: boolean;
    telegramEnabled: boolean;
    emailDisabled: boolean;
    telegramDisabled: boolean;
    emailSaving: boolean;
    telegramSaving: boolean;
  }> = [];

  protected readonly NOTIFICATION_TYPES = NOTIFICATION_TYPES;
  protected readonly NOTIFICATION_TYPE_LABELS = NOTIFICATION_TYPE_LABELS;
  protected readonly environment = environment;

  /** X admin collectionId for the current environment (used to show only current marketplace row). */
  private readonly currentXCollectionId =
    ENV_COLLECTION_TO_X_COLLECTION_ID[environment.collection] ?? 'original';

  constructor(
    private stateService: StateService,
    private http: HttpClient,
    private notificationsService: NotificationsService,
  ) {}

  ngOnInit(): void {
    combineLatest([
      this.stateService.web3Connected$,
      this.stateService.walletAddress$,
      this.stateService.authenticatedAsAddress$,
    ])
      .pipe(
        distinctUntilChanged(
          (prev, curr) =>
            prev[0] === curr[0] &&
            prev[1] === curr[1] &&
            prev[2] === curr[2],
        ),
        takeUntil(this.destroy$),
      )
      .subscribe(([isConnected, walletAddress, authenticatedAsAddress]) => {
        this.isConnected = isConnected;
        this.walletAddress = walletAddress;
        this.effectiveAddress = authenticatedAsAddress || walletAddress;

        if (isConnected && walletAddress) {
          this.xAdminVisible = this.isAdminWallet(walletAddress);
          this.handleXOauthCallbackParams();
          this.loadEmail();
          this.loadTelegramLinked();
          this.loadSubscriptions();
          if (this.xAdminVisible) {
            this.initCurrentXAdminRow();
            this.loadXAdminStatus();
          }
        } else {
          this.userEmail = '';
          this.canSaveEmail = false;
          this.telegramLinked = false;
          this.telegramLinkIdentifier = null;
          this.telegramLinkError = null;
          this.subscriptions = this.getEmptySubscriptions();
          this.updateMatrixRows();
          this.xAdminVisible = false;
          this.xAdminError = null;
          this.xAdminMessage = null;
          this.resetXCollectionStates();
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private getEmptySubscriptions(): Record<NotificationType, SubscriptionState> {
    const empty: Record<NotificationType, SubscriptionState> = {} as Record<
      NotificationType,
      SubscriptionState
    >;
    for (const type of NOTIFICATION_TYPES) {
      empty[type] = { email: false, telegram: false };
    }
    return empty;
  }

  private getBaseUrl(): string {
    const graphUri = environment.alternativeListingsGraphURI || '';
    return graphUri.replace(/\/graphql$/, '');
  }

  private isAdminWallet(walletAddress: string): boolean {
    const adminWallets = environment.adminWallets ?? [];
    const normalized = walletAddress.toLowerCase();
    return adminWallets.some((address) => address.toLowerCase() === normalized);
  }

  private resetXCollectionStates(): void {
    this.currentXAdminRow = null;
  }

  private initCurrentXAdminRow(): void {
    const label = X_COLLECTION_LABELS[this.currentXCollectionId] ?? this.currentXCollectionId;
    this.currentXAdminRow = {
      collectionId: this.currentXCollectionId,
      label,
      connected: false,
      connecting: false,
      disconnecting: false,
      testing: false,
    };
  }

  private handleXOauthCallbackParams(): void {
    if (typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search);
    const status = params.get('x_oauth_status');
    if (!status) return;

    const collection = params.get('x_oauth_collection');
    const username = params.get('x_oauth_user');
    const error = params.get('x_oauth_error');

    if (status === 'success') {
      this.xAdminMessage = `X account connected${collection ? ` for ${collection}` : ''}${username ? ` (@${username})` : ''}.`;
      this.xAdminError = null;
    } else {
      this.xAdminError = `X OAuth failed${error ? `: ${error}` : ''}`;
      this.xAdminMessage = null;
    }

    params.delete('x_oauth_status');
    params.delete('x_oauth_collection');
    params.delete('x_oauth_user');
    params.delete('x_oauth_error');
    const query = params.toString();
    const nextUrl = `${window.location.pathname}${query ? `?${query}` : ''}${window.location.hash || ''}`;
    window.history.replaceState({}, document.title, nextUrl);
  }

  async loadXAdminStatus(): Promise<void> {
    if (!this.xAdminVisible) return;
    const baseUrl = this.getBaseUrl();
    const jwtToken = this.stateService.getJwtToken();
    if (!baseUrl || !jwtToken) return;

    this.xAdminLoading = true;
    this.xAdminError = null;
    try {
      const response = await firstValueFrom(
        this.http.get<{
          collections: Array<{ collectionId: string; connected: boolean; username?: string; userId?: string }>;
        }>(`${baseUrl}/api/x-admin/status`, {
          headers: { Authorization: `Bearer ${jwtToken}` },
        }),
      );
      const server = response.collections.find((c) => c.collectionId === this.currentXCollectionId);
      if (this.currentXAdminRow) {
        this.currentXAdminRow = {
          ...this.currentXAdminRow,
          connected: !!server?.connected,
          username: server?.username,
        };
      }
    } catch (error) {
      const msg = error instanceof HttpErrorResponse ? error.error?.message || error.message : 'Failed to load X admin status';
      this.xAdminError = Array.isArray(msg) ? msg[0] : msg;
    } finally {
      this.xAdminLoading = false;
    }
  }

  async connectX(collectionId: string): Promise<void> {
    if (!this.xAdminVisible) return;
    const baseUrl = this.getBaseUrl();
    const jwtToken = this.stateService.getJwtToken();
    if (!baseUrl || !jwtToken) return;

    this.xAdminError = null;
    this.xAdminMessage = null;
    if (this.currentXAdminRow?.collectionId === collectionId) {
      this.currentXAdminRow = { ...this.currentXAdminRow, connecting: true };
    }

    try {
      const response = await firstValueFrom(
        this.http.post<{ url: string }>(
          `${baseUrl}/api/x-admin/oauth/start`,
          { collectionId },
          {
            headers: {
              Authorization: `Bearer ${jwtToken}`,
              'Content-Type': 'application/json',
            },
          },
        ),
      );
      if (response?.url) {
        window.location.href = response.url;
        return;
      }
      this.xAdminError = 'Could not start X OAuth flow';
    } catch (error) {
      const msg = error instanceof HttpErrorResponse ? error.error?.message || error.message : 'Failed to start X OAuth flow';
      this.xAdminError = Array.isArray(msg) ? msg[0] : msg;
    } finally {
      if (this.currentXAdminRow?.collectionId === collectionId) {
        this.currentXAdminRow = { ...this.currentXAdminRow, connecting: false };
      }
    }
  }

  async disconnectX(collectionId: string): Promise<void> {
    if (!this.xAdminVisible) return;
    const baseUrl = this.getBaseUrl();
    const jwtToken = this.stateService.getJwtToken();
    if (!baseUrl || !jwtToken) return;

    this.xAdminError = null;
    if (this.currentXAdminRow?.collectionId === collectionId) {
      this.currentXAdminRow = { ...this.currentXAdminRow, disconnecting: true };
    }

    try {
      await firstValueFrom(
        this.http.post<{ success: boolean }>(
          `${baseUrl}/api/x-admin/disconnect`,
          { collectionId },
          {
            headers: {
              Authorization: `Bearer ${jwtToken}`,
              'Content-Type': 'application/json',
            },
          },
        ),
      );
      this.xAdminMessage = `Disconnected X account for ${collectionId}.`;
      await this.loadXAdminStatus();
    } catch (error) {
      const msg = error instanceof HttpErrorResponse ? error.error?.message || error.message : 'Failed to disconnect X account';
      this.xAdminError = Array.isArray(msg) ? msg[0] : msg;
    } finally {
      if (this.currentXAdminRow?.collectionId === collectionId) {
        this.currentXAdminRow = { ...this.currentXAdminRow, disconnecting: false };
      }
    }
  }

  async sendXTestPost(collectionId: string): Promise<void> {
    if (!this.xAdminVisible) return;
    const baseUrl = this.getBaseUrl();
    const jwtToken = this.stateService.getJwtToken();
    if (!baseUrl || !jwtToken) return;

    this.xAdminError = null;
    if (this.currentXAdminRow?.collectionId === collectionId) {
      this.currentXAdminRow = { ...this.currentXAdminRow, testing: true };
    }
    try {
      await firstValueFrom(
        this.http.post<{ success: boolean }>(
          `${baseUrl}/api/x-admin/tweet/test`,
          { collectionId },
          {
            headers: {
              Authorization: `Bearer ${jwtToken}`,
              'Content-Type': 'application/json',
            },
          },
        ),
      );
      this.xAdminMessage = `Test X stash bid posted for ${collectionId}.`;
    } catch (error) {
      const msg = error instanceof HttpErrorResponse ? error.error?.message || error.message : 'Failed to post test X stash bid';
      this.xAdminError = Array.isArray(msg) ? msg[0] : msg;
    } finally {
      if (this.currentXAdminRow?.collectionId === collectionId) {
        this.currentXAdminRow = { ...this.currentXAdminRow, testing: false };
      }
    }
  }

  private readonly MIN_LOADING_MS = 300;

  async loadEmail(): Promise<void> {
    if (!this.walletAddress) return;

    this.loadingEmail = true;
    const minDelay = new Promise<void>((r) => setTimeout(r, this.MIN_LOADING_MS));
    try {
      const graphUri = environment.alternativeListingsGraphURI || '';
      const baseUrl = graphUri.replace(/\/graphql$/, '');
      const jwtToken = this.stateService.getJwtToken();

      if (!baseUrl || !jwtToken) {
        this.userEmail = '';
        return;
      }

      const response = await firstValueFrom(
        this.http.get<{ email: string | null }>(`${baseUrl}/api/auth/email`, {
          headers: { Authorization: `Bearer ${jwtToken}` },
        }),
      );
      this.userEmail = response.email || '';
      this.canSaveEmail = this.isValidEmail(this.userEmail);
      this.updateMatrixRows();
    } catch {
      this.userEmail = '';
      this.canSaveEmail = false;
    } finally {
      await minDelay;
      this.loadingEmail = false;
    }
  }

  async confirmSaveEmail(): Promise<void> {
    if (!this.walletAddress || !this.userEmail || !this.isValidEmail(this.userEmail)) return;
    if (this.loadingEmail) return;

    this.loadingEmail = true;
    const minDelay = new Promise<void>((r) => setTimeout(r, this.MIN_LOADING_MS));
    try {
      const baseUrl = this.getBaseUrl();
      const jwtToken = this.stateService.getJwtToken();
      if (!baseUrl || !jwtToken) return;

      await firstValueFrom(
        this.http.post<{ success: boolean }>(
          `${baseUrl}/api/auth/email`,
          { email: this.userEmail },
          {
            headers: {
              Authorization: `Bearer ${jwtToken}`,
              'Content-Type': 'application/json',
            },
          },
        ),
      );
    } catch (error) {
      console.error('Error saving email:', error);
    } finally {
      await minDelay;
      this.loadingEmail = false;
    }
  }

  onEmailChange(value: string): void {
    this.userEmail = value;
    this.canSaveEmail = this.isValidEmail(value);
  }

  async confirmUnsubscribeEmail(event: Event): Promise<void> {
    event.preventDefault();
    if (!this.walletAddress || this.loadingEmail) return;

    this.loadingEmail = true;
    const minDelay = new Promise<void>((r) => setTimeout(r, this.MIN_LOADING_MS));
    try {
      const baseUrl = this.getBaseUrl();
      const jwtToken = this.stateService.getJwtToken();
      if (!baseUrl || !jwtToken) return;

      await firstValueFrom(
        this.http.post<{ success: boolean }>(
          `${baseUrl}/api/auth/email`,
          { email: '' },
          {
            headers: {
              Authorization: `Bearer ${jwtToken}`,
              'Content-Type': 'application/json',
            },
          },
        ),
      );
      this.userEmail = '';
      this.canSaveEmail = false;
      this.updateMatrixRows();
    } catch (error) {
      console.error('Error unsubscribing email:', error);
    } finally {
      await minDelay;
      this.loadingEmail = false;
    }
  }

  private isValidEmail(email: string): boolean {
    if (!email || email.trim() === '') return false;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email.trim());
  }

  private getErrorMessage(error: unknown, fallbackMessage: string): string {
    if (error instanceof HttpErrorResponse) {
      const payload = error.error;
      const rawMessage =
        payload?.message ??
        payload?.error ??
        error.message ??
        fallbackMessage;
      return Array.isArray(rawMessage) ? rawMessage[0] : String(rawMessage);
    }

    if (error instanceof Error && error.message) {
      return error.message;
    }

    return fallbackMessage;
  }

  async loadTelegramLinked(): Promise<void> {
    const baseUrl = this.getBaseUrl();
    const jwtToken = this.stateService.getJwtToken();
    const collectionId = environment.telegramCollectionId || 'original';
    if (!baseUrl) {
      this.telegramLinked = false;
      this.telegramLinkIdentifier = null;
      this.telegramLinkError = 'Telegram notifications are not configured right now.';
      this.updateMatrixRows();
      return;
    }
    if (!jwtToken) {
      this.telegramLinked = false;
      this.telegramLinkIdentifier = null;
      this.telegramLinkError = 'Please reconnect your wallet to manage Telegram notifications.';
      this.updateMatrixRows();
      return;
    }

    try {
      const response = await firstValueFrom(
        this.http.get<{ linked: boolean | Record<string, boolean>; linkId?: string | null }>(
          `${baseUrl}/api/auth/telegram?collectionId=${encodeURIComponent(collectionId)}`,
          { headers: { Authorization: `Bearer ${jwtToken}` } },
        ),
      );
      this.telegramLinked =
        typeof response.linked === 'boolean'
          ? response.linked
          : !!response.linked?.[collectionId];
      this.telegramLinkIdentifier = this.telegramLinked ? response.linkId ?? null : null;
      this.telegramLinkError = null;
      this.updateMatrixRows();
    } catch (error) {
      this.telegramLinked = false;
      this.telegramLinkIdentifier = null;
      this.telegramLinkError = this.getErrorMessage(
        error,
        'Failed to load Telegram link status. Please refresh and try again.',
      );
      this.updateMatrixRows();
    }
  }

  async confirmLinkTelegram(): Promise<void> {
    if (!this.telegramLinkCode?.trim() || this.loadingTelegramLink) return;

    const baseUrl = this.getBaseUrl();
    const jwtToken = this.stateService.getJwtToken();
    if (!baseUrl) {
      this.telegramLinkError = 'Telegram notifications are not configured right now.';
      return;
    }
    if (!jwtToken) {
      this.telegramLinkError = 'Please reconnect your wallet before linking Telegram.';
      return;
    }

    this.loadingTelegramLink = true;
    this.telegramLinkError = null;
    try {
      await firstValueFrom(
        this.http.post<{ success: boolean }>(
          `${baseUrl}/api/auth/telegram/link`,
          {
            code: this.telegramLinkCode.trim(),
            collectionId: environment.telegramCollectionId || 'original',
          },
          {
            headers: {
              Authorization: `Bearer ${jwtToken}`,
              'Content-Type': 'application/json',
            },
          },
        ),
      );
      this.telegramLinkCode = '';
      await this.loadTelegramLinked();
    } catch (error) {
      this.telegramLinkError = this.getErrorMessage(error, 'Failed to link Telegram');
    } finally {
      this.loadingTelegramLink = false;
    }
  }

  async confirmUnlinkTelegram(): Promise<void> {
    if (this.loadingTelegramUnlink) return;

    const baseUrl = this.getBaseUrl();
    const jwtToken = this.stateService.getJwtToken();
    if (!baseUrl) {
      this.telegramLinkError = 'Telegram notifications are not configured right now.';
      return;
    }
    if (!jwtToken) {
      this.telegramLinkError = 'Please reconnect your wallet before unlinking Telegram.';
      return;
    }

    this.loadingTelegramUnlink = true;
    this.telegramLinkError = null;
    try {
      await firstValueFrom(
        this.http.post<{ success: boolean }>(
          `${baseUrl}/api/auth/telegram/unlink`,
          { collectionId: environment.telegramCollectionId || 'original' },
          {
            headers: {
              Authorization: `Bearer ${jwtToken}`,
              'Content-Type': 'application/json',
            },
          },
        ),
      );
      this.telegramLinked = false;
      this.telegramLinkIdentifier = null;
      this.updateMatrixRows();
    } catch (error) {
      this.telegramLinkError = this.getErrorMessage(error, 'Failed to unlink Telegram');
    } finally {
      this.loadingTelegramUnlink = false;
    }
  }

  /** Personal bot URL (link account to receive bid notifications on owned punks). Set once from env. */
  telegramBotUrl = environment.telegramBotUrl || '';
  /** Public bot/channel URL (all events: sales, listings). Read-only link to join. */
  telegramPublicBotUrl = environment.telegramPublicBotUrl || '';
  publicNotificationChannels: PublicNotificationChannelConfig[] = this.buildPublicNotificationChannels();

  private buildPublicNotificationChannels(): PublicNotificationChannelConfig[] {
    const configured = (environment.publicNotificationChannels ?? [])
      .filter((channel) => !!channel.url?.trim())
      .map((channel) => ({
        ...channel,
        url: channel.url.trim(),
      }));
    if (configured.length > 0) {
      return configured;
    }

    const fallback: PublicNotificationChannelConfig[] = [];
    if (this.telegramPublicBotUrl) {
      fallback.push({
        type: 'telegram',
        title: 'Telegram',
        subtitle: 'Marketplace notifications',
        description: FALLBACK_PUBLIC_CHANNEL_DESCRIPTIONS.telegram,
        url: this.telegramPublicBotUrl,
      });
    }

    fallback.push({
      type: 'discord',
      title: 'Discord',
      subtitle: 'Marketplace community',
      description: FALLBACK_PUBLIC_CHANNEL_DESCRIPTIONS.discord,
      url: 'https://discord.com/invite/A9cv4wwVQd',
    });
    fallback.push({
      type: 'x',
      title: 'X',
      subtitle: 'Bot feed',
      description: FALLBACK_PUBLIC_CHANNEL_DESCRIPTIONS.x,
      url: 'https://x.com/punkbot_ce',
    });

    return fallback;
  }

  async loadSubscriptions(): Promise<void> {
    try {
      this.loadingSubscriptions = true;
      const result = await this.notificationsService.getSubscriptions();
      this.subscriptions = result.subscriptions;
      this.updateMatrixRows();
    } catch {
      this.subscriptions = this.getEmptySubscriptions();
      this.updateMatrixRows();
    } finally {
      this.loadingSubscriptions = false;
    }
  }

  private updateMatrixRows(): void {
    this.matrixRows = NOTIFICATION_TYPES.map((type) => ({
      type,
      label: NOTIFICATION_TYPE_LABELS[type],
      emailEnabled: this.subscriptions[type]?.email ?? true,
      telegramEnabled: this.subscriptions[type]?.telegram ?? true,
      emailDisabled: !this.userEmail,
      telegramDisabled: !this.telegramLinked,
      emailSaving:
        this.savingSubscription?.type === type && this.savingSubscription?.channel === 'email',
      telegramSaving:
        this.savingSubscription?.type === type && this.savingSubscription?.channel === 'telegram',
    }));
  }

  openMinAmountLimitModal(): void {
    this.minAmountLimitModalOpen = true;
  }

  closeMinAmountLimitModal(): void {
    this.minAmountLimitModalOpen = false;
  }

  async toggleSubscription(type: NotificationType, channel: 'email' | 'telegram'): Promise<void> {
    if (channel === 'email' && !this.userEmail) return;
    if (channel === 'telegram' && !this.telegramLinked) return;
    if (this.savingSubscription) return;

    const current = this.subscriptions[type]?.[channel] ?? false;
    const newValue = !current;

    // Optimistic update
    this.subscriptions = { ...this.subscriptions };
    if (!this.subscriptions[type]) this.subscriptions[type] = {};
    this.subscriptions[type] = { ...this.subscriptions[type], [channel]: newValue };

    this.savingSubscription = { type, channel };
    this.updateMatrixRows();
    try {
      await this.notificationsService.updateSubscriptions(this.subscriptions);
    } catch (error) {
      // Revert on error
      this.subscriptions[type] = { ...this.subscriptions[type], [channel]: current };
      console.error('Error updating subscription:', error);
    } finally {
      this.savingSubscription = null;
      this.updateMatrixRows();
    }
  }
}
